import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';

// Account workspace is six parallel fetches (per the locked TRACON V2 spec
// decision — there is no aggregated /account/:idOrSlug endpoint today).
// Same-origin HTTP/2 keeps the round-trip cost low. Each sub-resource is
// also exported individually so consumers can refresh one panel without
// re-pulling the whole workspace.

function enc(idOrSlug) {
  return encodeURIComponent(idOrSlug);
}

export function resolveAccount(idOrSlug) {
  return inFlightDedup(`account:resolve:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/resolve`)
  );
}

export function getAccountFleetAndRoster(idOrSlug) {
  return inFlightDedup(`account:fleet-and-roster:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/fleet-and-roster`)
  );
}

// The SAME contract as the account endpoint, scoped to one deal. The deal
// workspace must never render the account-wide roster: every pilot on any
// qualifying deal was tagged current there, so a sibling term's pilot showed
// up as a current pilot on a deal he was never on (Uinta 2026-07-29).
export function getDealFleetAndRoster(dealId) {
  return inFlightDedup(`deal:fleet-and-roster:${dealId}`, () =>
    api.get(`/api/tracon/deal/${enc(dealId)}/fleet-and-roster`)
  );
}

export function getAccountDocuments(idOrSlug) {
  return inFlightDedup(`account:documents:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/documents`)
  );
}

export function getAccountTrustLedger(idOrSlug) {
  return inFlightDedup(`account:trust-ledger:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/trust-ledger`)
  );
}

// Optional `deal` scopes the feed to ONE of the account's deals (§C3 — the
// focused-deal Activity panel must not show sibling deals' events; the server
// intersects with the account's own deal ids).
export function getAccountCommsTimeline(idOrSlug, deal) {
  const suffix = deal ? `?deal=${encodeURIComponent(deal)}` : '';
  return inFlightDedup(`account:comms-timeline:${idOrSlug}:${deal || 'all'}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/comms-timeline${suffix}`)
  );
}

export function getAccountStats(idOrSlug) {
  return inFlightDedup(`account:stats:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/stats`)
  );
}

// Task M — the account's policies grouped current vs historical (by Policy_End
// / stage) for the account-overview term sections.
// CLIENT-RECORD-SPEC §2: the record read model. Every fact carries
// { value, state, source, tier, writer }; the page renders, never derives.
export function getAccountRecord(idOrSlug) {
  return inFlightDedup(`account:record:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/record?_t=${Date.now()}`)
  );
}

export function getAccountTerms(idOrSlug) {
  return inFlightDedup(`account:terms:${idOrSlug}`, () =>
    api.get(`/api/tracon/account/${enc(idOrSlug)}/terms`)
  );
}

export function getCommercialDealBuild(dealId) {
  return inFlightDedup(`commercial:build:${dealId}`, () =>
    api.get(`/api/tracon/commercial/${encodeURIComponent(dealId)}/build`)
  );
}

// PATCH the insured name (Accounts.Account_Name) + optional Policyholder_Type.
// Account_Name is the canonical insured display, so this re-titles every term
// under the account. body: { account_name, policyholder_type? }.
export function updateNamedInsured(accountId, body) {
  return api.patch(`/api/tracon/account/${enc(accountId)}/named-insured`, body);
}

// The FEIN door (FEIN-SPEC.md): the broker states it, Supabase only.
export function updateAccountFein(accountId, fein) {
  return api.patch(`/api/tracon/account/${enc(accountId)}/fein`, { fein });
}

// Radar-backed address suggestions (server-proxied; empty when unconfigured).
export function suggestAddresses(q) {
  return api.get(`/api/tracon/address-autocomplete?q=${encodeURIComponent(q)}`);
}

// Typeahead for the primary-contact picker: this account's Contacts first,
// then the wider CRM, each row flagged on_account.
export function searchAccountContacts(accountId, q) {
  return api.get(`/api/tracon/account/${enc(accountId)}/contact-picker?q=${encodeURIComponent(q)}`);
}

// Set WHO the account's primary contact is. body: { contact_id } for an
// existing Contact, or { create: { first_name, last_name, email?, phone? } }
// to mint one parented to this account. The server cascades Deal.Contact_Name
// and the session contact copy through the one existing writer.
export function setPrimaryContact(accountId, body) {
  return api.post(`/api/tracon/account/${enc(accountId)}/primary-contact`, body);
}

// Add a pilot / co-owner to a deal (surgical append → Approved_Pilots + PHF +
// invite). body: { first_name, last_name, email, phone?, aircraft_ids?,
// interest?, resolution? }. interest='Owner' marks a co-owner. A 409 carries a
// conflict descriptor (name_match_no_email | email_in_use) the caller resolves
// by re-posting with `resolution`. See routes/tracon.routes.js add-pilot.
export function addPilotToDeal(dealId, body) {
  return api.post(`/api/tracon/deal/${enc(dealId)}/add-pilot`, body);
}

// Paper-PHF holding area (PAPER-PHF-HOLDING): paper submissions persist with
// no CRM Contact and wait for the broker. Confirm links/creates the Contact
// and lands the pilot on the roster through applyPilot (a 409 carries the
// same conflict descriptors as add-pilot); dismiss flips the row to removed.
export function listPaperPHFs(dealId) {
  return api.get(`/api/tracon/deal/${enc(dealId)}/paper-phfs?_t=${Date.now()}`);
}

export function confirmPaperPHF(dealId, phfId, body = {}) {
  return api.post(`/api/tracon/deal/${enc(dealId)}/paper-phfs/${enc(phfId)}/confirm`, body);
}

export function dismissPaperPHF(dealId, phfId, body = {}) {
  return api.post(`/api/tracon/deal/${enc(dealId)}/paper-phfs/${enc(phfId)}/dismiss`, body);
}

// Remove a pilot from a deal entirely — deletes their Approved_Pilots rows +
// marks the PHF removed. Distinct from "exclude" (named-excluded operator).
// addByEndorsement: the policy binds with the attested pilots and this one is
// added later by endorsement (recorded on the form; PILOT-ATTESTATION.md).
export function removePilotFromDeal(dealId, contactId, { addByEndorsement = false } = {}) {
  return api.del(`/api/tracon/deal/${enc(dealId)}/pilot/${enc(contactId)}${addByEndorsement ? '?add_by_endorsement=true' : ''}`);
}

// Parallel-fetches all six. Returns the shape consumed by AccountWorkspace.
// Each panel can also call its individual fetcher to refresh in isolation.
export function getAccountWorkspace(idOrSlug) {
  return inFlightDedup(`account:workspace:${idOrSlug}`, async () => {
    const [resolve, fleetAndRoster, documents, trustLedger, commsTimeline, stats] =
      await Promise.all([
        resolveAccount(idOrSlug),
        getAccountFleetAndRoster(idOrSlug),
        getAccountDocuments(idOrSlug),
        getAccountTrustLedger(idOrSlug),
        getAccountCommsTimeline(idOrSlug),
        getAccountStats(idOrSlug),
      ]);
    return { resolve, fleetAndRoster, documents, trustLedger, commsTimeline, stats };
  });
}
